<template>
    <v-data-table
        dense
        class="table"
        mobile-breakpoint="0"
        :items-per-page="50"
        :items="items"
        no-data-text="No applications found"
        :headers="headers"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100, -1] }"
    >
        <template #item.application="{ item: { id, name, ns, color } }">
            <div class="application">
                <div class="status" :class="color" />
                <div class="name">
                    <router-link :to="link(id, undefined)">{{ name }}</router-link>
                    <span v-if="ns" class="caption grey--text"> (ns:{{ ns }})</span>
                </div>
            </div>
        </template>
        <template #item.cluster="{ item: { cluster } }">
            <span class="cluster grey--text"> {{ cluster }} </span>
        </template>
        <template #item.type="{ item: { id, type } }">
            <div v-if="type" class="d-flex align-center">
                <AppIcon :icon="type.icon" class="mr-1" />

                <router-link v-if="type.report" :to="link(id, type.report)" class="type" :class="type.status">
                    {{ type.name }}
                </router-link>
                <div v-else class="type">
                    {{ type.name }}
                </div>
            </div>
        </template>
        <template #item.errors="{ item: { id, errors: param } }">
            <router-link :to="link(id, 'SLO')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.latency="{ item: { id, latency: param } }">
            <router-link :to="link(id, 'SLO')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.upstreams="{ item: { id, upstreams: param } }">
            <router-link :to="link(id, 'SLO')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.instances="{ item: { id, instances: param } }">
            <router-link :to="link(id, 'Instances')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.restarts="{ item: { id, restarts: param } }">
            <router-link :to="link(id, 'Instances')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.cpu="{ item: { id, cpu: param } }">
            <router-link :to="link(id, 'CPU')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.memory="{ item: { id, memory: param } }">
            <router-link :to="link(id, 'Memory')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.disk_io_load="{ item: { id, disk_io_load: param } }">
            <router-link :to="link(id, 'Storage')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.disk_usage="{ item: { id, disk_usage: param } }">
            <router-link :to="link(id, 'Storage')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.network="{ item: { id, network: param } }">
            <router-link :to="link(id, 'Net')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.dns="{ item: { id, dns: param } }">
            <router-link :to="link(id, 'DNS')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.logs="{ item: { id, logs: param } }">
            <router-link :to="link(id, 'Logs', { query: JSON.stringify({ source: 'agent', view: 'patterns' }) })" class="logs">
                <div class="value">{{ param.value || '–' }}</div>
                <v-sparkline
                    v-if="param.chart"
                    :value="param.chart.map((v) => (v === null ? 0 : v))"
                    fill
                    smooth
                    padding="4"
                    :color="`blue ${$vuetify.theme.dark ? '' : 'lighten-4'}`"
                    height="30"
                    width="120"
                />
            </router-link>
        </template>
    </v-data-table>
</template>

<script>
import AppIcon from '@/components/AppIcon.vue';

export default {
    components: { AppIcon },

    props: {
        headers: Array,
        items: Array,
    },

    methods: {
        link(id, report, query) {
            return { name: 'overview', params: { view: 'applications', id, report }, query: { ...query, ...this.$utils.contextQuery() } };
        },
    },
};
</script>

<style scoped>
.table:deep(table) {
    min-width: 500px;
}
.table:deep(tr:hover) {
    background-color: unset !important;
}
.table:deep(th) {
    white-space: nowrap;
}
.table:deep(th),
.table:deep(td) {
    padding: 4px 8px !important;
}
.table:deep(td:has(.application)) {
    padding-left: 0 !important;
}
.table .application {
    display: flex;
    gap: 4px;
}
.table .application .status {
    height: 20px;
    width: 4px;
}
.table .application .name {
    max-width: 30ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .application .cluster {
    max-width: 20ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .logs {
    display: block;
    position: relative;
    height: 100%;
    color: inherit;
}
.table .logs .value {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
}
.table:deep(td:has(.logs)) {
    width: 120px;
    min-width: 120px;
    padding: 0 !important;
}

.type {
    opacity: 60%;
    white-space: nowrap;
}
.type.unknown {
    opacity: 100%;
    border-bottom: 2px solid #ab47bc !important;
    background-color: unset !important;
}
.type.ok {
    opacity: 100%;
}
.type.critical,
.type.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}

.value {
    color: inherit;
    opacity: 60%;
    white-space: nowrap;
}
.value.critical,
.value.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}
</style>
